/**
 * AC-1479: `resolveDotPath(payload, path)` - safe dot-path resolver for
 * QueueItem payload projection.
 *
 * Walks `path.split('.')` segments using own-property bracket lookups so that
 * prototype-chain keys cannot resolve. This is the same shape the backend
 * uses for payload-field discovery (see AC-1477) so the frontend's projection
 * matches what the server-side dropdown surfaces.
 *
 * ## Contract
 *
 *   - Returns `undefined` when:
 *       - `path` is the empty string
 *       - `payload` is `null` or `undefined`
 *       - any segment is missing on its parent object
 *       - traversal reaches a primitive (e.g. number, string) before the
 *         path is fully consumed
 *       - any intermediate node is an array (the backend skips arrays at
 *         one level of nesting; we mirror that here so what the user
 *         picked from the discovery dropdown is what they see)
 *       - the segment names a key that exists only on the prototype chain
 *         (e.g. `__proto__`, `constructor`, `hasOwnProperty`). This is a
 *         security guard - without it, `resolveDotPath({}, '__proto__')`
 *         would return `Object.prototype` and JSON.stringify back as
 *         `[object Object]`.
 *
 *   - Returns the value verbatim when the path resolves to a falsy primitive
 *     (`0`, `false`, `''`). The cell renderer needs to distinguish "missing"
 *     (em-dash) from "falsy" (the value itself).
 *
 * ## Why own-property lookup, not bracket access alone
 *
 * Plain `obj[segment]` would happily return prototype-chain values
 * (`obj['toString']` resolves to `Object.prototype.toString`). We reject
 * inherited keys explicitly via `Object.prototype.hasOwnProperty.call(...)`
 * so user-supplied dot-paths cannot extract anything beyond the literal
 * payload structure.
 */
export function resolveDotPath(payload: unknown, path: string): unknown {
  if (path === '') return undefined;
  if (payload === null || payload === undefined) return undefined;

  const segments = path.split('.');
  let current: unknown = payload;

  for (const segment of segments) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    // Arrays are intentionally rejected so the projection stays consistent
    // with the backend's payload-field discovery, which does not descend
    // into arrays.
    if (Array.isArray(current)) return undefined;
    if (!Object.prototype.hasOwnProperty.call(current, segment)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}

export default resolveDotPath;
